import * as Sentry from "@sentry/react";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { broadcastQueryClient } from "@tanstack/query-broadcast-client-experimental";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import i18n from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import localforage from "localforage";
import React, { ComponentType, LazyExoticComponent, Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { initReactI18next } from "react-i18next";
import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import PageLoading from "./app/components/page-loading.tsx";
import PersistGate from "./app/components/persist-gate.tsx";
import EventsLayout from "./app/events/layout.tsx";
import RootLayout from "./app/layout.tsx";
import SettingsLayout from "./app/settings/layout.tsx";
import "./index.css";
import AccessTokenProvider from "./jsbridge/access-token-provider.tsx";

const AsyncComponent = ({ component: Component }: { component: LazyExoticComponent<ComponentType> }) => {
  return (
    <Sentry.ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Component />
      </Suspense>
    </Sentry.ErrorBoundary>
  );
};

dayjs.extend(LocalizedFormat);

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://7f7c2fa4bfd576d7534fe226543bd6d8@o4505185715748864.ingest.us.sentry.io/4507768171331584",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0.1,
  });
}

const sentryCreateHashRouter = import.meta.env.PROD
  ? Sentry.wrapCreateBrowserRouter(createBrowserRouter)
  : createBrowserRouter;

const router = sentryCreateHashRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        path: "",
        element: <AsyncComponent component={lazy(() => import("./app/(home)/page.tsx"))} />,
      },
      {
        path: "chat",
        element: <AsyncComponent component={lazy(() => import("./app/chat/page.tsx"))} />,
      },
      {
        path: "events",
        element: <EventsLayout />,
        children: [
          {
            path: "",
            element: <AsyncComponent component={lazy(() => import("./app/events/(list)/page.tsx"))} />,
          },
          {
            path: ":uuid",
            element: <AsyncComponent component={lazy(() => import("./app/events/[uuid]/page.tsx"))} />,
          },
        ],
      },
      {
        path: "settings",
        element: <SettingsLayout />,
        children: [
          {
            path: "",
            element: <AsyncComponent component={lazy(() => import("./app/settings/(home)/page.tsx"))} />,
          },
          {
            path: "account",
            element: <AsyncComponent component={lazy(() => import("./app/settings/account/page.tsx"))} />,
          },
          {
            path: "language",
            element: <AsyncComponent component={lazy(() => import("./app/settings/language/page.tsx"))} />,
          },
          {
            path: "timezone",
            element: <AsyncComponent component={lazy(() => import("./app/settings/timezone/page.tsx"))} />,
          },
          {
            path: "units",
            element: <AsyncComponent component={lazy(() => import("./app/settings/units/page.tsx"))} />,
          },
          {
            path: "about",
            element: <AsyncComponent component={lazy(() => import("./app/settings/about/page.tsx"))} />,
          },
          {
            path: "alerts",
            element: <AsyncComponent component={lazy(() => import("./app/settings/alerts/page.tsx"))} />,
          },
          {
            path: "calendars",
            element: <AsyncComponent component={lazy(() => import("./app/settings/calendars/page.tsx"))} />,
          },
          {
            path: "calendars/guide",
            element: <AsyncComponent component={lazy(() => import("./app/settings/calendars/guide/page.tsx"))} />,
          },
          {
            path: "calendars/switch/:provider",
            element: (
              <AsyncComponent component={lazy(() => import("./app/settings/calendars/switch/[provider]/page.tsx"))} />
            ),
          },
          {
            path: "calendars/connect/apple-calendar",
            element: (
              <AsyncComponent
                component={lazy(() => import("./app/settings/calendars/connect/apple-calendar/page.tsx"))}
              />
            ),
          },
        ],
      },
    ],
  },
]);

const cacheStore = localforage.createInstance({
  name: "react-query-cache",
});

const persister = createAsyncStoragePersister({
  storage: cacheStore,
});

// increase the number when the data structure changes
// to burst the cache
const buster = "1";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      gcTime: Infinity,
    },
  },
});

broadcastQueryClient({ queryClient });

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(resourcesToBackend((language: string, namespace: string) => import(`./messages/${namespace}/${language}.json`)))
  .init({
    lng: "en",
    fallbackLng: "en",
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  });

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister, buster }}>
      <PersistGate>
        <AccessTokenProvider>
          <RouterProvider router={router} />
        </AccessTokenProvider>
      </PersistGate>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
